import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type {
  DocumentoDeLaObra,
  FacturaCandidata,
  RenglonDelDocumento,
} from '@/lib/supabase/tipos-bd'

/**
 * LOS PAPELES DE LA OBRA.
 *
 * Cotizaciones, adicionales y facturas en una sola lista. Estaban en tres
 * sitios distintos de la misma pantalla —la principal arriba con sus
 * renglones, los adicionales como números sueltos, las facturas en ninguna
 * parte— para contestar una sola pregunta: qué papeles tiene esta obra.
 */
export function useDocumentosDeLaObra(ordenServicioId: string | null) {
  const consulta = useQuery({
    // COLGADA DE LA CLAVE DE LA ORDEN. Tenía su propia clave y quien enlazaba
    // un adicional no la conocía: la lista se quedaba enseñando lo de antes
    // —Johan enlazó la FEV 2823 y no le aparecía—. Bajo 'ordenes-servicio',
    // cualquier cosa que refresque la orden la refresca a ella.
    queryKey: ['ordenes-servicio', 'documentos', ordenServicioId],
    enabled: Boolean(ordenServicioId),
    queryFn: async (): Promise<DocumentoDeLaObra[]> => {
      const { data, error } = await supabase.rpc('fn_documentos_de_la_obra', {
        p_orden_servicio_id: ordenServicioId!,
      })

      if (error) throw new Error(mensajeDeError(error))

      return ((data ?? []) as unknown as DocumentoDeLaObra[]).map((d) => ({
        ...d,
        valor: d.valor === null ? null : Number(d.valor),
      }))
    },
  })

  return { documentos: consulta.data ?? [], cargando: consulta.isLoading }
}

/**
 * Lo que hay dentro de uno.
 *
 * Se pide SOLO del que se abre. Traer los renglones de todos los documentos
 * por si acaso es lo que vuelve lenta una pantalla que casi siempre se mira
 * de reojo.
 */
export function useRenglonesDelDocumento(clase: string | null, referencia: string | null) {
  const consulta = useQuery({
    queryKey: ['renglones-del-documento', clase, referencia],
    enabled: Boolean(clase && referencia),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<RenglonDelDocumento[]> => {
      const { data, error } = await supabase.rpc('fn_renglones_del_documento', {
        p_clase: clase!,
        p_referencia: referencia!,
      })

      if (error) throw new Error(mensajeDeError(error))

      return ((data ?? []) as unknown as RenglonDelDocumento[]).map((r) => ({
        ...r,
        cantidad: r.cantidad === null ? null : Number(r.cantidad),
        precio_unitario: r.precio_unitario === null ? null : Number(r.precio_unitario),
        iva: r.iva === null ? null : Number(r.iva),
        subtotal: r.subtotal === null ? null : Number(r.subtotal),
      }))
    },
  })

  return { renglones: consulta.data ?? [], cargando: consulta.isLoading }
}

/** Las facturas de World Office que podrían ser de esta obra. */
export function useFacturasParaEnlazar(
  ordenServicioId: string | null,
  opciones: { soloDelCliente: boolean; busqueda: string },
) {
  const busqueda = opciones.busqueda.trim()

  const consulta = useQuery({
    queryKey: ['facturas-para-enlazar', ordenServicioId, opciones.soloDelCliente, busqueda],
    enabled: Boolean(ordenServicioId),
    queryFn: async (): Promise<FacturaCandidata[]> => {
      const { data, error } = await supabase.rpc('fn_facturas_para_enlazar', {
        p_orden_servicio_id: ordenServicioId!,
        p_solo_del_cliente: opciones.soloDelCliente,
        p_busqueda: busqueda === '' ? null : busqueda,
      })

      if (error) throw new Error(mensajeDeError(error))

      return ((data ?? []) as unknown as FacturaCandidata[]).map((f) => ({
        ...f,
        total: f.total === null ? null : Number(f.total),
      }))
    },
  })

  return { candidatas: consulta.data ?? [], cargando: consulta.isLoading }
}

/** Lo que hay que refrescar cuando se cuelga o se quita una factura. */
function useRefrescarLaObra(ordenServicioId: string) {
  const clienteConsultas = useQueryClient()

  return async () => {
    await Promise.all(
      [
        ['ordenes-servicio', 'documentos', ordenServicioId],
        ['ordenes-servicio', 'totales', ordenServicioId],
        ['facturas-para-enlazar', ordenServicioId],
        ['ordenes-servicio'],
        // 0320: una FEV hecha directo, enlazada a una orden sin cotización,
        // hace nacer la orden de trabajo; el tablero tiene que verla.
        ['tablero'],
      ].map((clave) => clienteConsultas.invalidateQueries({ queryKey: clave })),
    )
  }
}

export function useEnlazarFactura(ordenServicioId: string) {
  const refrescar = useRefrescarLaObra(ordenServicioId)

  return useMutation({
    mutationFn: async ({ idAsiento, notas }: { idAsiento: number; notas?: string | null }) => {
      const { error } = await supabase.rpc('fn_enlazar_factura', {
        p_orden_servicio_id: ordenServicioId,
        p_wo_id_asiento: idAsiento,
        p_notas: notas ?? null,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

/**
 * Quitarle a una cotización la factura que se le amarró mal (0286). La FEV
 * 14301 se amarró sola, por productos, al adicional de la OS-2026-0113 y la
 * obra «subió» $637.663. Lo que se quita no se vuelve a amarrar solo.
 */
export function useQuitarFacturaDeCotizacion(ordenServicioId: string) {
  const refrescar = useRefrescarLaObra(ordenServicioId)

  return useMutation({
    mutationFn: async (cotizacionId: string) => {
      const { error } = await supabase.rpc('fn_quitar_factura_de_cotizacion', {
        p_cotizacion_id: cotizacionId,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

export function useDesenlazarFactura(ordenServicioId: string) {
  const refrescar = useRefrescarLaObra(ordenServicioId)

  return useMutation({
    mutationFn: async (idAsiento: number) => {
      const { error } = await supabase.rpc('fn_desenlazar_factura', {
        p_orden_servicio_id: ordenServicioId,
        p_wo_id_asiento: idAsiento,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}
